import { Drawer, Collapse, Table, Spin, Tooltip } from 'antd';
import { ExclamationCircleFilled, CloseOutlined, LoadingOutlined } from '@ant-design/icons';
import DownloadMultiSheetExcel from 'components/molecules/MicroIntractions/DownloadMultiSheetExcel';
import { colorConstants } from 'config/colorConstants';
import {
    ValidatePlanSection,
    ValidatePlanTable,
    ValidatePlanColumn,
    ValidatePlanRow,
    ValidatePlanSectionMetadata,
    ValidatePlanCellValue,
    isStructuredCellValue,
} from 'types/tpo/validatePlan';
import type { ColumnsType } from 'antd/es/table';
import { useState, useMemo } from 'react';
import styled from '@emotion/styled';

const { Panel } = Collapse;

// Styled Table wrapper for validate plan tables
const StyledValidatePlanTable = styled(Table)`
    background: white;
    border-radius: 8px;

    .ant-table {
        background: transparent;
    }

    .ant-table-thead > tr > th {
        background: #f0f0f0 !important;
        font-weight: 500;
        font-size: 13px;
        color: #4e4c55;
        border-bottom: 1px solid #f5f5f5;
        padding: 10px 12px;
        white-space: nowrap;
    }

    .ant-table-tbody > tr > td {
        font-size: 13px;
        padding: 10px 12px;
        border-bottom: 1px solid #f5f5f5;
        white-space: nowrap;
    }

    .ant-table-tbody > tr:last-child > td {
        border-bottom: none;
    }

    .ant-table-tbody > tr:hover > td {
        background: #fafafa;
    }

    .ant-table-column-sorter {
        color: #bfbfbf;
    }

    .ant-table-column-sorter-up.active,
    .ant-table-column-sorter-down.active {
        color: #44338a;
    }

    .ant-table-body {
        overflow: auto !important;
    }

    .ant-table-body::-webkit-scrollbar {
        width: 6px;
        height: 6px;
    }

    .ant-table-body::-webkit-scrollbar-thumb {
        background: #d9d9d9;
        border-radius: 3px;
    }

    .ant-table-body::-webkit-scrollbar-thumb:hover {
        background: #bfbfbf;
    }

    .ant-table-body::-webkit-scrollbar-track {
        background: #f5f5f5;
        border-radius: 3px;
    }
`;

interface ValidatePlanDrawerProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    sections: ValidatePlanSection[];
    width?: number;
    topOffset?: number;
    loading?: boolean;
    error?: boolean | null;
}

type SortOrder = 'ascend' | 'descend' | null;

interface SortState {
    columnKey: string | null;
    order: SortOrder;
}

const formatAsPercent = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(n)) return String(value);
    return `${Math.round(n)}%`;
};

const formatAsCurrency = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(n)) return String(value);

    return `$${n.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

// Helper to format observed value (handles arrays)
const formatObservedValue = (value: number | string | number[] | string[] | null): string => {
    if (value === null) return '-';

    if (Array.isArray(value)) {
        return value
            .map((v) => {
                const n = typeof v === 'number' ? v : Number(v);
                return Number.isFinite(n) ? n.toFixed(2) : String(v);
            })
            .join('-');
    }

    const n = typeof value === 'number' ? value : Number(value);
    if (Number.isFinite(n)) return n.toFixed(2);

    return String(value);
};

// Helper to format expected value for display
const formatExpectedValue = (
    expectedValue: ValidatePlanCellValue['expectedValue'],
    constraintName?: string,
    showFull: boolean = false,
): string => {
    if (!expectedValue) return '';

    const isRetailerMarginPerc = constraintName === 'RETAILER_MARGIN_PERC_CHECK';
    const isRetailerMarginAbs = constraintName === 'RETAILER_MARGIN_ABS_CHECK';

    if (expectedValue.type === 'value' && expectedValue.recommendedValue !== null) {
        let display: string;

        if (isRetailerMarginPerc) {
            display = formatAsPercent(expectedValue.recommendedValue);
        } else if (isRetailerMarginAbs) {
            display = formatAsCurrency(expectedValue.recommendedValue);
        } else {
            display = String(expectedValue.recommendedValue);
        }

        return `(${display})`;
    } else if (expectedValue.type === 'range' && expectedValue.min !== null && expectedValue.max !== null) {
        const minDisplay = isRetailerMarginPerc
            ? formatAsPercent(expectedValue.min)
            : isRetailerMarginAbs
            ? formatAsCurrency(expectedValue.min)
            : String(expectedValue.min);

        const maxDisplay = isRetailerMarginPerc
            ? formatAsPercent(expectedValue.max)
            : isRetailerMarginAbs
            ? formatAsCurrency(expectedValue.max)
            : String(expectedValue.max);

        return `(${minDisplay}-${maxDisplay})`;
    } else if (expectedValue.type === 'enum') {
        if (expectedValue.allowedValues && expectedValue.allowedValues.length > 0) {
            if (showFull) {
                return `(${expectedValue.allowedValues.join(', ')})`;
            }
            return `(${expectedValue.allowedValues.slice(0, 2).join(', ')}${
                expectedValue.allowedValues.length > 2 ? '...' : ''
            })`;
        }
        if (expectedValue.disallowedValues && expectedValue.disallowedValues.length > 0) {
            return `(${expectedValue.disallowedValues.join(', ')})`;
        }
    }
    return '';
};

// Helper to get tooltip content with colored text for a cell value
const getTooltipContent = (
    value: string | number | null | ValidatePlanCellValue,
    constraintName?: string,
): React.ReactNode => {
    if (value === null || value === '-') {
        return <span style={{ color: colorConstants.darkGrey }}>-</span>;
    }

    if (isStructuredCellValue(value)) {
        const { observedValue, expectedValue, hasExpectedValue } = value;
        const observedDisplay = formatObservedValue(observedValue);
        const expectedDisplay = formatExpectedValue(expectedValue, constraintName, true); // showFull for tooltip

        if (hasExpectedValue && expectedDisplay) {
            return (
                <span>
                    <span style={{ color: '#ef4444', fontWeight: 500 }}>{observedDisplay}</span>
                    <span style={{ color: '#9ca3af', marginLeft: 4 }}>{expectedDisplay}</span>
                </span>
            );
        }
        return <span style={{ color: colorConstants.darkGrey }}>{observedDisplay}</span>;
    }

    return <span style={{ color: colorConstants.darkGrey }}>{String(value)}</span>;
};

// Helper to render cell value with violation highlighting
const renderCellValue = (
    value: string | number | null | ValidatePlanCellValue,
    constraintName?: string,
): React.ReactNode => {
    // Handle null or dash values
    if (value === null || value === '-') {
        return <span style={{ color: '#9ca3af' }}>-</span>;
    }

    const tooltipContent = getTooltipContent(value, constraintName);

    // Handle structured cell values (from parser)
    if (isStructuredCellValue(value)) {
        const { observedValue, expectedValue, hasExpectedValue } = value;
        const observedDisplay = formatObservedValue(observedValue);
        const expectedDisplay = formatExpectedValue(expectedValue, constraintName);

        if (hasExpectedValue && expectedDisplay) {
            return (
                <Tooltip title={tooltipContent} placement="top" color="white" overlayStyle={{ borderRadius: 4 }}>
                    <span>
                        <span style={{ color: '#ef4444', fontWeight: 500 }}>{observedDisplay}</span>
                        <span style={{ color: '#9ca3af', marginLeft: 4 }}>{expectedDisplay}</span>
                    </span>
                </Tooltip>
            );
        }

        return (
            <Tooltip title={tooltipContent} placement="top" color="white" overlayStyle={{ borderRadius: 4 }}>
                <span>{observedDisplay}</span>
            </Tooltip>
        );
    }

    // Handle plain string/number values
    return (
        <Tooltip title={tooltipContent} placement="topLeft" color="white" overlayStyle={{ borderRadius: 4 }}>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{String(value)}</div>
        </Tooltip>
    );
};

const SectionTable = ({ table }: { table: ValidatePlanTable }) => {
    const [sortState, setSortState] = useState<SortState>({ columnKey: null, order: null });

    const columns: ColumnsType<ValidatePlanRow> = useMemo(
        () =>
            table.columns.map((col: ValidatePlanColumn, index: number) => ({
                title: col.label,
                dataIndex: col.key,
                key: col.key,
                sorter: true,
                sortOrder: sortState.columnKey === col.key ? sortState.order : null,
                render: (value: string | number | null) => renderCellValue(value, col.key),
                ellipsis: { showTitle: false },
                width: index === 0 ? 250 : 170, // First column wider, others have min width
            })),
        [table.columns, sortState],
    );

    // Calculate scroll width based on number of columns
    const scrollX = useMemo(() => {
        const firstColWidth = 250;
        const otherColsWidth = (table.columns.length - 1) * 140;
        return firstColWidth + otherColsWidth;
    }, [table.columns.length]);

    const handleTableChange = (_pagination: any, _filters: any, sorter: any) => {
        if (sorter.columnKey) {
            setSortState({
                columnKey: sorter.columnKey,
                order: sorter.order || null,
            });
        } else {
            setSortState({ columnKey: null, order: null });
        }
    };

    const sortedData = useMemo(() => {
        if (!sortState.columnKey || !sortState.order) {
            return table.rows;
        }

        const sorted = [...table.rows].sort((a, b) => {
            const aVal = String(a[sortState.columnKey!] ?? '');
            const bVal = String(b[sortState.columnKey!] ?? '');

            return sortState.order === 'ascend' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        });

        return sorted;
    }, [table.rows, sortState]);

    return (
        <div className="mb-4 last:mb-0">
            <StyledValidatePlanTable
                columns={columns as ColumnsType<any>}
                dataSource={sortedData.map((row, idx) => ({ ...row, key: idx }))}
                pagination={false}
                size="small"
                onChange={handleTableChange}
                scroll={{ x: scrollX, y: 300 }}
                showSorterTooltip={false}
            />
        </div>
    );
};

const PanelHeader = ({ title, severity }: { title: string; severity?: string }) => (
    <div className="flex items-center gap-2">
        {severity === 'error' && <ExclamationCircleFilled style={{ color: colorConstants.red, fontSize: 14 }} />}
        <span className="font-semibold">{title}</span>
    </div>
);

// Component to display metadata fields
const MetadataDisplay = ({ metadata }: { metadata?: ValidatePlanSectionMetadata }) => {
    const metadataEntries = Object.entries(metadata ?? {}).filter(([_, value]) => value !== undefined);

    if (metadataEntries.length === 0) {
        return null;
    }

    return (
        <div className="flex flex-wrap gap-x-6 gap-y-2 mb-3 text-sm">
            {metadataEntries.map(([key, value]) => (
                <div key={key} className="flex items-center gap-1">
                    <span className="text-gray-600">{key}:</span>
                    <span className="font-semibold text-gray-800">{String(value)}</span>
                </div>
            ))}
        </div>
    );
};

const getCellExportValue = (value: string | number | null | ValidatePlanCellValue, constraintName?: string): string => {
    if (value === null || value === undefined) return '';
    if (isStructuredCellValue(value)) {
        const observed = formatObservedValue(value.observedValue);
        const expected = value.hasExpectedValue ? formatExpectedValue(value.expectedValue, constraintName, true) : '';
        return expected ? `${observed} ${expected}` : observed;
    }
    return String(value);
};

const ValidatePlanDrawer = ({
    open,
    onClose,
    title = 'Validate plan against library',
    sections,
    width = 850,
    topOffset = 0,
    loading = false,
    error = null,
}: ValidatePlanDrawerProps) => {
    const exportSheets = useMemo(
        () =>
            sections?.map((section) => {
                const rows: (string | number)[][] = [];

                const metadataEntries = Object.entries(section.metadata ?? {}).filter(([, v]) => v !== undefined);
                if (metadataEntries.length > 0) {
                    metadataEntries.forEach(([key, value]) => rows.push([key, String(value)]));
                    rows.push([]);
                }

                section.tables.forEach((table, tableIndex) => {
                    if (tableIndex > 0) rows.push([]);
                    rows.push(table.columns.map((col) => col.label));
                    table.rows.forEach((row) => {
                        rows.push(table.columns.map((col) => getCellExportValue(row[col.key], col.key)));
                    });
                });

                return { name: section.title, rows };
            }) ?? [],
        [sections],
    );

    // Sections with errors should be expanded by default
    const defaultActiveKeys = useMemo(() => {
        const errorSections = sections?.filter((section) => section.severity === 'error')?.map((section) => section.id);
        if (errorSections && errorSections.length > 0) {
            return errorSections;
        }
        // If no error sections, expand the first section
        return sections?.length > 0 ? [sections[0].id] : [];
    }, [sections]);

    const loadingIcon = <LoadingOutlined style={{ fontSize: 32, color: colorConstants.violet }} spin />;

    return (
        <Drawer
            title={
                <div className="flex justify-between items-center w-full">
                    <span className="font-semibold text-base">{title}</span>
                    <CloseOutlined onClick={onClose} className="cursor-pointer text-gray-500 hover:text-gray-700" />
                </div>
            }
            placement="right"
            onClose={onClose}
            open={open}
            width={width}
            closable={false}
            bodyStyle={{ padding: '16px' }}
            headerStyle={{ borderBottom: 'none', padding: '16px 24px' }}
            contentWrapperStyle={{ top: topOffset, height: `calc(100% - ${topOffset}px)` }}
            maskStyle={{ top: topOffset }}
        >
            {/* Legend + Export */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                        <div style={{ width: 32, height: 8, background: '#ef4444' }}></div>
                        <span className="text-sm text-gray-600">Violated Value</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div style={{ width: 32, height: 8, background: '#9ca3af' }}></div>
                        <span className="text-sm text-gray-600">Library Value</span>
                    </div>
                </div>
                <DownloadMultiSheetExcel
                    sheets={exportSheets}
                    fileName={title}
                    disabled={loading || !!error}
                />
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-full">
                    <Spin indicator={loadingIcon} />
                </div>
            ) : error ? (
                <div className="flex flex-col items-center justify-center h-full text-center px-6">
                    <span className="text-lg font-semibold text-gray-800 mb-2">Something went wrong</span>
                    <span className="text-sm text-gray-500">Unable to validate the plan. Please try again later.</span>
                </div>
            ) : (
                <Collapse
                    defaultActiveKey={defaultActiveKeys}
                    expandIconPosition="end"
                    bordered={false}
                    style={{ background: 'transparent' }}
                >
                    {sections?.map((section) => (
                        <Panel
                            key={section.id}
                            header={<PanelHeader title={section.title} severity={section.severity} />}
                            style={{
                                marginBottom: 12,
                                background: '#f5f5f5',
                                borderRadius: 8,
                                border: 'none',
                            }}
                        >
                            <div className="rounded-md p-3">
                                <MetadataDisplay metadata={section.metadata} />
                                {section.tables.map((table) => (
                                    <SectionTable key={table.id} table={table} />
                                ))}
                            </div>
                        </Panel>
                    ))}
                </Collapse>
            )}
        </Drawer>
    );
};

export default ValidatePlanDrawer;
